import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, Square, Ban, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { uploadSelfie, useClock, useShiftCurrent } from '@/hooks/useShiftClock'
import { useWarmPosition } from '@/hooks/useWarmPosition'
import { PositionError, WAIT_CAP, isFresh, startPositionRead, type PositionRead } from '@/lib/clockPosition'
import { reportShiftClockFailed } from '@/lib/shiftClockReport'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import type { ClockLocation } from '@/hooks/useTimesheet'

/**
 * EP-154: the clock at the top of the dashboard for staff and admin. One
 * button in three stages (owner, 2026-10-06):
 *
 *   1. Clock in: primary. The label alone, also after a shift closed today.
 *   2. Clock out: outlined primary. The label alone.
 *   3. Cannot clock in: red and inert, the text naming the issue (location
 *      refused, or what the server answered).
 *
 * The position is read in two stages and warmed up (see `lib/clockPosition`):
 * while the permission is already granted the card keeps a quiet fix from
 * mount and from each return to the foreground (`useWarmPosition`, it never
 * prompts). The tap starts a fresh staged read (low accuracy, 8 s, and high
 * accuracy in parallel) and opens the front camera at the same time. When the
 * photo comes back the card uses, in this order: a fix the tap-time read
 * already has, a warm fix younger than 5 minutes, else it waits for the read
 * for at most 30 s. Then it uploads the selfie and posts the clock, the
 * button spinning meanwhile and saying whether it is finding the location or
 * saving. While it is finding the location a Cancel control stops the wait and
 * nothing is sent; once the upload starts there is no Cancel, because a
 * request is on its way. Both proofs are required (owner, 2026-10-06): a
 * refused position is stage 3, a position not found in time is a passing
 * notice, a cancelled camera is nothing.
 *
 * Every failure that is not a camera cancel (position denied or not found,
 * upload, clock) is reported once to Sentry as `shift_clock_failed`, tags only.
 *
 * Measured on an iPhone 15 Pro (2026-10-07), and the reason for the shape
 * above: while WebKit's own "Allow / Don't Allow" location sheet is pending,
 * `navigator.permissions.query` reports geolocation as `denied`, so the
 * Permissions API must never put the button into stage 3 by itself; only a
 * real PERMISSION_DENIED from a position read may. And a read started right
 * after the camera sheet closes never answers, while one started at the tap
 * does, so the read starts at the tap. The Permissions API is used to clear a
 * refusal early when it reports `granted`, and to warm up only when it does.
 *
 * On a phone the button is the full width; from lg it sits at the right of
 * the column.
 */

// Server answers the card has words for; anything else reads as "failed".
const KNOWN_ERRORS = ['shift_already_open', 'no_open_shift', 'shift_too_long', 'shift_too_short', 'clock_proof_required', 'forbidden', 'no_position']

// Full width and md on a phone; from lg the kit's sm, beside the building
// picker (owner, 2026-10-07: lg was too big on a desktop, then md was; lg was
// too big on a phone too).
const BUTTON = 'w-full h-auto py-2 lg:w-auto lg:min-w-36 lg:h-8 lg:py-0'
// Stage 3 carries a second line, so it keeps its own height on a desktop.
const BLOCKED = `${BUTTON} lg:h-auto lg:py-1.5 opacity-100!`

// Clears a tap-time refusal when the browser reports the permission granted
// (its change event, where supported) or when the app comes back to the
// foreground after the person turned location on in Settings.
function useRefusalRecovery(clear: () => void) {
  useEffect(() => {
    const onForeground = () => {
      if (document.visibilityState === 'visible') clear()
    }
    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('focus', onForeground)

    let status: PermissionStatus | null = null
    let cancelled = false
    const permissions = typeof navigator !== 'undefined' ? navigator.permissions : undefined
    if (permissions?.query) {
      permissions
        .query({ name: 'geolocation' })
        .then((s) => {
          if (cancelled) return
          status = s
          s.onchange = () => {
            if (s.state === 'granted') clear()
          }
        })
        .catch(() => undefined)
    }
    return () => {
      cancelled = true
      if (status) status.onchange = null
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('focus', onForeground)
    }
  }, [clear])
}

function Label({ main, sub }: { main: ReactNode; sub?: ReactNode }) {
  return (
    <span className="flex flex-col items-center leading-tight min-w-0">
      <span>{main}</span>
      {sub && <span className="text-xs font-normal opacity-80 whitespace-normal">{sub}</span>}
    </span>
  )
}

// A little more room between the icon and the words than the kit's gap.
const ICON = 'w-4 h-4 shrink-0 mr-2'

export function ShiftClockCard() {
  const { t } = useTranslation('common')
  const { data, isLoading } = useShiftCurrent()
  const clock = useClock()
  const size = useIsDesktop() ? 'sm' : 'md'

  // idle, then 'locating' (the photo is in, the position is awaited) and
  // 'saving' (upload and clock in flight).
  const [phase, setPhase] = useState<'idle' | 'locating' | 'saving'>('idle')
  const busy = phase !== 'idle'
  // A refusal found at a position read (sticky until the permission is
  // granted or the app comes back to the foreground), and a server answer
  // or a failed read, which clear on their own after a moment.
  const [refused, setRefused] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const readAtTap = useRef<PositionRead | null>(null)
  const runId = useRef(0)
  const warm = useWarmPosition()

  const [clearRefusal] = useState(() => () => setRefused(false))
  useRefusalRecovery(clearRefusal)

  useEffect(() => {
    if (!serverError) return
    const id = window.setTimeout(() => setServerError(null), 6000)
    return () => window.clearTimeout(id)
  }, [serverError])

  const open = data?.open ?? null
  const action: 'in' | 'out' = open ? 'out' : 'in'

  // The tap: start the staged position read now (it runs while the camera is
  // up) and open the camera. Its failure is reported when the photo comes
  // back, or never, if the camera is cancelled.
  const openCamera = () => {
    readAtTap.current?.cancel()
    readAtTap.current = startPositionRead()
    fileInput.current?.click()
  }

  // Stops the wait for the position: nothing has been sent yet.
  const cancel = () => {
    runId.current += 1
    readAtTap.current?.cancel()
    readAtTap.current = null
    setPhase('idle')
    if (fileInput.current) fileInput.current.value = ''
  }

  // A fix at hand (tap-time read, then a fresh warm one), else wait for the read.
  const locate = (read: PositionRead): Promise<ClockLocation> => {
    const now = read.peek()
    if (now) return Promise.resolve(now)
    const cached = warm.current
    if (cached && isFresh(cached)) return Promise.resolve(cached)
    return read.fix(WAIT_CAP)
  }

  // The photo is back from the camera: position, upload, clock, in one go.
  const submit = async (selfie: File) => {
    const id = (runId.current += 1)
    const cancelled = () => runId.current !== id
    setPhase('locating')
    setServerError(null)
    const read = readAtTap.current ?? startPositionRead()
    readAtTap.current = null
    let location: ClockLocation
    try {
      location = await locate(read)
    } catch (e) {
      if (cancelled()) return
      const denied = e instanceof PositionError && e.reason === 'denied'
      reportShiftClockFailed(denied ? 'position_denied' : 'position_timeout', action)
      if (denied) setRefused(true)
      else setServerError('no_position')
      finish()
      return
    }
    if (cancelled()) return
    setPhase('saving')
    let stage: 'upload' | 'clock' = 'upload'
    try {
      const photo_key = await uploadSelfie(selfie)
      stage = 'clock'
      await clock.mutateAsync({ clock: action, photo_key, location })
    } catch (e) {
      reportShiftClockFailed(stage, action)
      const code = (e as { response?: { data?: { error?: { code?: string } } } }).response?.data?.error?.code
      setServerError(code && KNOWN_ERRORS.includes(code) ? code : 'failed')
    }
    finish()
  }

  const finish = () => {
    setPhase('idle')
    if (fileInput.current) fileInput.current.value = ''
  }

  // Waiting for the position, the button itself cancels (owner, 2026-10-25:
  // no separate Cancel button). Once the upload starts nothing can be called
  // back, so saving stays disabled.
  const locating = phase === 'locating'
  const busyLabel = locating
    ? <Label main={t('dashboard.shift.locating')} sub={t('dashboard.shift.tap_to_cancel')} />
    : t('dashboard.shift.saving')
  const tapAction = locating ? cancel : openCamera
  const busyIcon = (icon: ReactNode) => (locating ? <Loader2 className={`${ICON} animate-spin`} /> : icon)

  const button = () => {
    if (refused || serverError) {
      // Inert through the kit's non-native path (a span with aria-disabled),
      // not the native attribute: DaisyUI paints a natively disabled button
      // grey, and this one is meant to read red.
      return (
        <Button as="span" role="button" disabled variant="error" size={size} className={BLOCKED}
          leftIcon={<Ban className={ICON} />} data-testid="shift-blocked">
          <Label
            main={action === 'in' ? t('dashboard.shift.cannot_clock_in') : t('dashboard.shift.cannot_clock_out')}
            sub={refused ? t('dashboard.shift.issue_location') : t(`dashboard.shift.errors.${serverError}`)}
          />
        </Button>
      )
    }
    if (open) {
      // Outlined, on a white ground (owner, 2026-10-07: not see-through on
      // the grey page); the hover fill of a primary button is kept.
      return (
        <Button variant="primary" outline size={size} className={`${BUTTON} bg-base-100 hover:bg-primary`}
          onClick={tapAction} loading={phase === 'saving'}
          leftIcon={busyIcon(<Square className={ICON} />)} data-testid="shift-clock-out">
          {busy ? busyLabel : t('dashboard.shift.clock_out')}
        </Button>
      )
    }
    return (
      <Button variant="primary" size={size} className={BUTTON}
        onClick={tapAction} loading={phase === 'saving'}
        leftIcon={busyIcon(<Play className={ICON} />)} data-testid="shift-clock-in">
        {busy ? busyLabel : t('dashboard.shift.clock_in')}
      </Button>
    )
  }

  return (
    <div data-testid="shift-clock-card" className="flex flex-col gap-1 lg:flex-row lg:items-center lg:justify-end lg:gap-2">
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        data-testid="shift-selfie-input"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void submit(file)
        }}
      />
      {isLoading ? <div className="h-10 lg:h-8 animate-pulse rounded-lg bg-base-200 lg:w-36" /> : button()}
    </div>
  )
}
